import { Link } from "@remix-run/react";
import { motion } from "framer-motion";

import { Authors } from "~/components/blog/authors";
import { Date } from "~/components/blog/date";
import { HorizontalSeparator } from "~/components/blog/horizontal-separator";
import type { Post } from "~/lib/post";

type Props = Readonly<{
  post: Post;
  readingTime: string;
}>;

const item = {
  hidden: { y: 25, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      delay: 0.15,
    },
  },
};

export function PostPreview({ post, readingTime }: Props): JSX.Element {
  return (
    <motion.li
      className="prose dark:prose-invert m-0 p-0"
      itemProp="blogPost"
      itemScope={false}
      itemType="https://schema.org/BlogPosting"
      variants={item}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <Link
        prefetch="intent"
        to={`/blog/${post.slug}`}
        className="no-underline"
      >
        <div className="font-semibold flex flex-col gap-y-6 p-6 lg:p-8 m-0 border border-primary hover:border-gray-400 hover:dark:border-gray-600 global-hover-transition bg-secondary round">
          <h2 className="h3 m-0">{post.title}</h2>
          <div className="sm:flex flex-row items-center gap-x-4 md:gap-x-6 text-secondary font-normal">
            <div className="flex flex-row items-center gap-x-4 md:gap-x-6 mb-4 sm:mb-0">
              <Date dateString={post.date} />
              <HorizontalSeparator />
              <div className="whitespace-nowrap">
                {post.timeOverride
                  ? `${post.timeOverride} min read`
                  : readingTime}
              </div>
            </div>
            <div className="hidden sm:inline-flex items-center h-[6px]">
              <HorizontalSeparator />
            </div>
            <Authors authors={post.authors} />
          </div>
          <p className="text-secondary font-normal m-0">{post.excerpt}</p>
        </div>
      </Link>
    </motion.li>
  );
}
